import { useCallback, useState } from "react";
import { isApiError } from "@/api/errors";
import {
  precheckMeeting,
  uploadMeeting,
  type CreateMeetingFields,
} from "@/api/meetings";
import type { UUID } from "@/api/types";
import {
  completeUpload,
  getUploadState,
  initUpload,
  uploadChunk,
} from "@/api/uploads";
import { streamSha256 } from "@/lib/sha256-streamer";

// ADR-020: файлы >500 МБ идут через chunked. Меньшие — legacy multipart
// (одна POST-форма). Граница условная: 500 МБ безопасно влезает в RAM
// сервера через `await file.read()` и сетевой обрыв на single-POST
// ~50 МБ некритичен.
const CHUNKED_THRESHOLD_BYTES = 500 * 1024 * 1024;

export interface UploadResult {
  meeting_id: UUID;
  forced_new: boolean;
}

export interface DuplicateState {
  exists: true;
  meeting_id: UUID;
}

export interface UseUploadState {
  hashing: boolean;
  uploading: boolean;
  progressPercent: number | null;
  duplicate: DuplicateState | null;
  error: string | null;
}

export interface StartUploadInput {
  file: File;
  fields: Omit<CreateMeetingFields, "force_new">;
  /** When true, skip pre-check and post with force_new=true. */
  forceNew?: boolean;
}

export interface UseUpload {
  state: UseUploadState;
  startUpload: (input: StartUploadInput) => Promise<UploadResult | null>;
  reset: () => void;
  clearDuplicate: () => void;
}

const INITIAL: UseUploadState = {
  hashing: false,
  uploading: false,
  progressPercent: null,
  duplicate: null,
  error: null,
};

export function describeError(err: unknown): string {
  if (isApiError(err)) {
    if (err.code === "network_error") return "errors.network";
    // 507 from the storage layer: the server-side message is an English
    // botocore string ("XMinioStorageFull … minimum free drive threshold"),
    // useless to an operator. Nothing the user can do but tell an admin.
    if (err.code === "storage_full") return "errors.storageFull";
    return err.message;
  }
  // NotReadableError: браузер потерял доступ к File после выбора в пикере —
  // файл изменили/переместили/удалили, он занят другой программой или лежит
  // в негидратированной облачной папке (OneDrive/Диск). Повторное чтение того
  // же handle бесполезно — нужен повторный выбор файла.
  if (err instanceof DOMException && err.name === "NotReadableError") {
    return "errors.fileNotReadable";
  }
  if (err instanceof Error) return err.message;
  return "errors.unknown";
}

/**
 * Orchestrates the FR-1..FR-9 sequence:
 *  1) Stream SHA-256 of the file (FR-4).
 *  2) POST /api/meetings/precheck — if `exists` and not `forceNew`, surface the
 *     duplicate state and return null. Caller decides whether to re-invoke
 *     with `forceNew: true`.
 *  3) POST /api/meetings multipart with progress (XHR-based; see meetings.ts).
 */
export function useUpload(): UseUpload {
  const [state, setState] = useState<UseUploadState>(INITIAL);

  const reset = useCallback(() => setState(INITIAL), []);
  const clearDuplicate = useCallback(
    () => setState((s) => ({ ...s, duplicate: null })),
    [],
  );

  const startUpload = useCallback(
    async ({ file, fields, forceNew = false }: StartUploadInput) => {
      setState({
        hashing: true,
        uploading: false,
        progressPercent: 0,
        duplicate: null,
        error: null,
      });
      let sha256: string;
      try {
        // Hashing a multi-GB recording takes ~15 s, so it reports progress into
        // the same percent field the upload uses — otherwise the dialog looks
        // frozen before a single byte leaves the browser.
        sha256 = await streamSha256(file, {
          onProgress: (processed, totalBytes) => {
            setState((s) => ({
              ...s,
              progressPercent:
                totalBytes > 0
                  ? Math.min(100, Math.round((processed / totalBytes) * 100))
                  : 100,
            }));
          },
        });
      } catch (err) {
        setState({ ...INITIAL, error: describeError(err) });
        return null;
      }
      setState((s) => ({ ...s, hashing: false }));

      if (!forceNew) {
        try {
          const probe = await precheckMeeting({ sha256 });
          if (probe.exists && probe.meeting_id) {
            setState({
              ...INITIAL,
              duplicate: { exists: true, meeting_id: probe.meeting_id },
            });
            return null;
          }
        } catch (err) {
          setState({ ...INITIAL, error: describeError(err) });
          return null;
        }
      }

      setState((s) => ({ ...s, uploading: true, progressPercent: 0 }));

      // ADR-020 path для крупных файлов.
      if (file.size >= CHUNKED_THRESHOLD_BYTES) {
        try {
          const result = await chunkedUploadFlow({
            file,
            sha256,
            fields,
            forceNew,
            onProgress: (pct) =>
              setState((s) => ({ ...s, progressPercent: pct })),
          });
          setState({ ...INITIAL, progressPercent: 100 });
          return result;
        } catch (err) {
          // 409 от /init = дубль (если backend вернул existing_meeting_id —
          // мы уже бросили DuplicateAbort выше).
          if (err instanceof DuplicateAbort) {
            setState({
              ...INITIAL,
              duplicate: { exists: true, meeting_id: err.meetingId },
            });
            return null;
          }
          setState({ ...INITIAL, error: describeError(err) });
          return null;
        }
      }

      // Legacy multipart path (<500 МБ).
      try {
        const result = await uploadMeeting(
          file,
          { ...fields, force_new: forceNew, sha256_hint: sha256 },
          {
            onProgress: (loaded, total) => {
              const pct = total > 0 ? Math.round((loaded / total) * 100) : 0;
              setState((s) => ({ ...s, progressPercent: pct }));
            },
          },
        );
        setState({ ...INITIAL, progressPercent: 100 });
        return { meeting_id: result.id, forced_new: forceNew };
      } catch (err) {
        setState({ ...INITIAL, error: describeError(err) });
        return null;
      }
    },
    [],
  );

  return { state, startUpload, reset, clearDuplicate };
}

// ---------------------------------------------------------------------------
// Chunked upload flow (ADR-020)
// ---------------------------------------------------------------------------

class DuplicateAbort extends Error {
  constructor(public readonly meetingId: UUID) {
    super("duplicate");
  }
}

interface ChunkedFlowInput {
  file: File;
  sha256: string;
  fields: Omit<CreateMeetingFields, "force_new">;
  forceNew: boolean;
  onProgress: (percent: number) => void;
}

async function chunkedUploadFlow({
  file,
  sha256,
  fields,
  forceNew,
  onProgress,
}: ChunkedFlowInput): Promise<UploadResult> {
  // 1. Init — резервирует MinIO multipart, возвращает upload_id + chunk_size.
  //    409 с existing_meeting_id если дубль (если только !forceNew).
  let init;
  try {
    init = await initUpload({
      original_filename: file.name,
      expected_size: file.size,
      expected_sha256: sha256,
      folder_id: fields.folder_id ?? null,
    });
  } catch (err) {
    if (!forceNew && isApiError(err) && err.status === 409) {
      const existing = err.details?.existing_meeting_id;
      if (typeof existing === "string") throw new DuplicateAbort(existing);
    }
    throw err;
  }

  const { upload_id, chunk_size } = init;

  // 2. Залить все чанки последовательно. Параллельный аплоад дал бы
  //    выигрыш на быстрых каналах, но усложняет порядок ETag'ов и
  //    progress-аггрегацию — для v1 sequential.
  //
  // Resume policy: после chunk-ошибки делаем один re-sync через
  //    GET /api/uploads/{id} → дальше пропускаем уже принятые offset'ы.
  //    Сетевая флапа на одном из 400 чанков (4 ГБ / 10 МБ) не убивает
  //    весь upload — клиент бесшовно подхватывает с того же места.
  let receivedOffsets = new Set<number>();
  let attemptedRetry = false;
  let uploaded = 0;

  for (let offset = 0; offset < file.size; offset += chunk_size) {
    if (receivedOffsets.has(offset)) {
      uploaded = Math.min(offset + chunk_size, file.size);
      onProgress(Math.round((uploaded / file.size) * 100));
      continue;
    }
    const end = Math.min(offset + chunk_size, file.size);
    const blob = file.slice(offset, end);
    try {
      await uploadChunk(upload_id, offset, blob);
    } catch (err) {
      if (attemptedRetry) throw err;
      attemptedRetry = true;
      // Re-sync state и продолжить с того же offset'а.
      const state = await getUploadState(upload_id);
      receivedOffsets = new Set(state.received_offsets);
      // Если этот offset уже принят сервером (race на 204 + сетевой
      // обрыв до ack), просто пропускаем.
      if (!receivedOffsets.has(offset)) {
        await uploadChunk(upload_id, offset, blob);
      }
    }
    receivedOffsets.add(offset);
    uploaded = end;
    onProgress(Math.round((uploaded / file.size) * 100));
  }

  // 3. Complete — финализирует MinIO multipart, создаёт Meeting,
  //    запускает phase-2 Celery task. Возвращает MeetingSummary.
  const meeting = await completeUpload(upload_id, {
    meeting_type_id: fields.meeting_type_id,
    client_id: fields.client_id,
    participants: fields.participants,
    folder_id: fields.folder_id ?? null,
    force_new: forceNew,
  });
  return { meeting_id: meeting.id, forced_new: forceNew };
}
